import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
    Avatar, Typography, IconButton, InputBase, CircularProgress, Divider,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import http, { apiErrorMessage } from '../../Api/http';
import { GetChatUsers, SearchUsers, UpdateGroupMembers } from '../../Api/Api';
import { solidBtn, ghostBtn } from '../uiKit';
import { PRIMARY } from '../../theme';
import { avatarToneFor, initialsFrom } from './chatHelpers';
import { selectIsMasterAdmin } from '../../redux/slices/authSlice';
import { selectActiveEntityId } from '../../redux/slices/orgSlice';

// See CreateGroupDialog.jsx — same doc §4.1 rule: `entityId` is required on
// SearchUsers/GetChatUsers for MasterAdmin only, and only once it's a real
// numeric backend entity id.
const numericEntityId = (id) => (/^\d+$/.test(String(id ?? '')) ? String(id) : null);

/** Add + remove members in one all-or-nothing UpdateGroupMembers call, per
 * the integration doc. Starts from the group's current member set and diffs
 * against the final selection to build the Add/Remove CSVs. */
export default function ManageMembersDialog({ open, onClose, group, currentMembers, onSaved }) {
    const isMasterAdmin = useSelector(selectIsMasterAdmin);
    const activeEntityId = useSelector(selectActiveEntityId);
    const entityIdForRequest = isMasterAdmin ? numericEntityId(activeEntityId) : null;
    const [query, setQuery] = useState('');
    const [directory, setDirectory] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const searchTimer = useRef(null);
    const originalIds = useRef(new Set());

    useEffect(() => {
        if (!open) return;
        const ids = new Set((currentMembers || []).map((m) => m.userId));
        originalIds.current = ids;
        setSelectedIds(new Set(ids));
        setQuery('');
        setLoadingUsers(true);
        http.get(GetChatUsers, { params: entityIdForRequest ? { entityId: entityIdForRequest } : undefined }).then(({ data: body }) => {
            setDirectory(Array.isArray(body?.data) ? body.data : (body?.data?.items || []));
        }).catch(() => setDirectory([])).finally(() => setLoadingUsers(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setLoadingUsers(true);
            const call = query.trim()
                ? http.get(SearchUsers, { params: { query: query.trim(), ...(entityIdForRequest ? { entityId: entityIdForRequest } : {}) } })
                : http.get(GetChatUsers, { params: entityIdForRequest ? { entityId: entityIdForRequest } : undefined });
            call.then(({ data: body }) => setDirectory(Array.isArray(body?.data) ? body.data : (body?.data?.items || [])))
                .catch(() => {})
                .finally(() => setLoadingUsers(false));
        }, 300);
        return () => clearTimeout(searchTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    const toggle = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const submit = async () => {
        const add = [...selectedIds].filter((id) => !originalIds.current.has(id));
        const remove = [...originalIds.current].filter((id) => !selectedIds.has(id));
        if (add.length === 0 && remove.length === 0) { onClose(); return; }
        setSaving(true);
        try {
            const body2 = { GroupId: group.groupId };
            if (add.length) body2.Add = add.join(',');
            if (remove.length) body2.Remove = remove.join(',');
            const { data: body } = await http.put(UpdateGroupMembers, body2);
            if (body?.error) throw new Error(body.message || 'Could not update members.');
            toast.success('Members updated.');
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not update members.'));
        } finally {
            setSaving(false);
        }
    };

    // Union of the fetched directory and the group's current members, so a
    // member not present in a narrowed search result doesn't disappear from
    // the list just for being already selected.
    const byId = new Map();
    (currentMembers || []).forEach((m) => byId.set(m.userId, { userId: m.userId, name: m.name, designation: m.designation }));
    directory.forEach((u) => byId.set(u.userId ?? u.id, { userId: u.userId ?? u.id, name: u.name || u.userName, designation: u.designation }));
    const rows = [...byId.values()];

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A', flex: 1 }}>Manage members</Typography>
                <IconButton size="small" onClick={onClose}><CloseRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '4px !important' }}>
                <Stack spacing={1.4}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '9px', px: 1.4, height: 38 }}>
                        <SearchRoundedIcon sx={{ fontSize: 17, color: '#98A0AE' }} />
                        <InputBase placeholder="Search people…" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                    </Stack>
                    <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{selectedIds.size} member{selectedIds.size === 1 ? '' : 's'} selected</Typography>
                    <Divider />
                    <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #EEF0F6', borderRadius: '9px' }}>
                        {loadingUsers ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
                        ) : rows.length === 0 ? (
                            <Typography sx={{ fontSize: 12.5, color: '#98A0AE', textAlign: 'center', py: 3 }}>No users found.</Typography>
                        ) : (
                            rows.map((u) => {
                                const isCreator = group?.creatorUserId === u.userId;
                                const on = selectedIds.has(u.userId);
                                const tone = avatarToneFor(u.name);
                                return (
                                    <Stack
                                        key={u.userId}
                                        direction="row" spacing={1.2}
                                        onClick={() => (!isCreator ? toggle(u.userId) : null)}
                                        sx={{ alignItems: 'center', px: 1.4, py: 0.9, cursor: isCreator ? 'default' : 'pointer', borderBottom: '1px solid #F4F5F9', opacity: isCreator ? 0.75 : 1, '&:hover': { bgcolor: isCreator ? 'transparent' : '#F8F8FC' } }}
                                    >
                                        <Avatar sx={{ width: 30, height: 30, ...tone, fontSize: 11, fontWeight: 800 }}>{initialsFrom(u.name)}</Avatar>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }} noWrap>{u.name}{isCreator ? ' (creator)' : ''}</Typography>
                                            {u.designation && <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{u.designation}</Typography>}
                                        </Box>
                                        {on && (
                                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <CheckRoundedIcon sx={{ fontSize: 13, color: '#fff' }} />
                                            </Box>
                                        )}
                                    </Stack>
                                );
                            })
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                <Button onClick={submit} disabled={saving} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                    {saving ? 'Saving…' : 'Save changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
